import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { orgAdmin } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { StatCard, PageHeader, Btn, LoadingSpinner } from "../../components/ui";

export default function OrgOverviewPage() {
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const orgId     = user?.organisationId;

  const [overview, setOverview] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!orgId) {
      // Try to fetch org from API if not in auth context
      orgAdmin.getMyOrg().then(org => {
        if (!org?.id) { setLoading(false); return; }
        return Promise.all([
          orgAdmin.reportOverview(org.id),
          orgAdmin.getBranches(org.id),
        ]).then(([ov, br]) => {
          setOverview(ov.data);
          setBranches(br.data?.results || br.data || []);
        });
      }).catch(() => {}).finally(() => setLoading(false));
      return;
    }
    Promise.all([
      orgAdmin.reportOverview(orgId),
      orgAdmin.getBranches(orgId),
    ]).then(([ov, br]) => {
      setOverview(ov.data);
      setBranches(br.data?.results || br.data || []);
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, [orgId]);

  return (
    <div style={styles.root}>
      <PageHeader
        title={user?.organisationName || "Organisation Overview"}
        subtitle={`Welcome back, ${user?.name}. Here's your organisation at a glance.`}
        action={<Btn onClick={() => navigate("/org/branches")}>Manage Branches →</Btn>}
      />

      {loading ? <LoadingSpinner /> : overview && (
        <>
          <div style={styles.statsRow}>
            <StatCard label="Total Branches"    value={overview.total_branches}       color="#8CBCE0" icon="🏢" />
            <StatCard label="Active Branches"   value={overview.active_branches}      color="#22C55E" icon="✅" />
            <StatCard label="Tickets Today"     value={overview.tickets_today}        color="#A78BFA" icon="🎫" />
            <StatCard label="Completed Today"   value={overview.completed_today}      color="#22C55E" icon="✓" />
            <StatCard label="Waiting Now"       value={overview.waiting_now}          color="#EAB308" icon="⏳" />
            <StatCard label="Completion Rate"   value={overview.completion_rate_today != null ? `${overview.completion_rate_today}%` : null} color="#F472B6" icon="📈" />
          </div>

          {/* Live branch cards */}
          <h2 style={styles.sectionTitle}>Branches — Live Status</h2>
          <div style={styles.branchGrid}>
            {branches.map(branch => (
              <BranchCard
                key={branch.id}
                branch={branch}
                onManage={() => navigate(`/org/branches/${branch.id}/manage`)}
              />
            ))}
            {branches.length === 0 && (
              <div style={styles.empty}>
                <span style={{ fontSize: "32px" }}>🏢</span>
                <p style={{ color: "var(--text-muted)" }}>No branches yet. Add your first branch.</p>
                <Btn onClick={() => navigate("/org/branches")}>+ Add Branch</Btn>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function BranchCard({ branch, onManage }) {
  const statusColor = {
    active: "#22C55E", trial: "#EAB308", suspended: "#EF4444"
  }[branch.status] || "var(--text-secondary)";

  return (
    <div style={styles.branchCard}>
      <div style={styles.branchCardTop}>
        <div>
          <div style={styles.branchName}>{branch.name}</div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "4px" }}>
            <span style={{ ...styles.statusDot, background: statusColor }} />
            <span style={{ fontSize: "12px", color: statusColor, fontWeight: "600", textTransform: "capitalize" }}>{branch.status}</span>
          </div>
          {branch.admin_name && (
            <div style={styles.branchAdmin}>Admin: {branch.admin_name}</div>
          )}
          {!branch.admin_name && (
            <div style={{ ...styles.branchAdmin, color: "#EF4444" }}>No admin assigned</div>
          )}
        </div>
        {branch.unread_log_count > 0 && (
          <div style={styles.logBadge}>{branch.unread_log_count}</div>
        )}
      </div>

      <div style={styles.branchStats}>
        <div style={styles.branchStat}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: "#8CBCE0" }}>{branch.tickets_today ?? 0}</div>
          <div style={styles.branchStatLabel}>tickets today</div>
        </div>
        <div style={styles.branchStat}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: "#EAB308" }}>{branch.waiting_now ?? 0}</div>
          <div style={styles.branchStatLabel}>waiting now</div>
        </div>
        <div style={styles.branchStat}>
          <div style={{ fontSize: "20px", fontWeight: "800", color: "#22C55E" }}>
            {branch.completion_rate != null ? `${branch.completion_rate}%` : "—"}
          </div>
          <div style={styles.branchStatLabel}>completion</div>
        </div>
      </div>

      <Btn variant="ghost" onClick={onManage} style={{ width: "100%", justifyContent: "center" }}>
        Manage Branch →
      </Btn>
    </div>
  );
}

const styles = {
  root:        { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1200px" },
  statsRow:    { display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "32px" },
  sectionTitle:{ fontSize: "13px", fontWeight: "700", color: "var(--text-muted)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "14px" },
  branchGrid:  { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))", gap: "14px" },
  branchCard:  { background: "var(--surface-bg)", border: "1px solid rgba(64,141,201,0.18)", borderRadius: "14px", padding: "20px", display: "flex", flexDirection: "column", gap: "16px" },
  branchCardTop:{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" },
  branchName:  { fontSize: "16px", fontWeight: "700", color: "var(--text-primary)" },
  statusDot:   { width: "7px", height: "7px", borderRadius: "50%", flexShrink: 0 },
  branchAdmin: { fontSize: "12px", color: "var(--text-muted)", marginTop: "4px" },
  logBadge:    { background: "#EF4444", color: "#fff", borderRadius: "12px", padding: "2px 8px", fontSize: "11px", fontWeight: "700", flexShrink: 0 },
  branchStats: { display: "flex", gap: "0", borderTop: "1px solid var(--surface-border)", borderBottom: "1px solid var(--surface-border)", padding: "12px 0" },
  branchStat:  { flex: 1, textAlign: "center" },
  branchStatLabel: { fontSize: "10px", color: "var(--text-muted)", fontWeight: "600", letterSpacing: "0.05em", textTransform: "uppercase", marginTop: "2px" },
  empty:       { gridColumn: "1/-1", textAlign: "center", padding: "48px", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" },
};
